import { useEffect, useMemo, useRef, useState } from "react";
import axios from "../axiosInstance";
import { MultiSelect } from "primereact/multiselect";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { ConfirmDialog } from "primereact/confirmdialog";

import {
  MdDelete,
  MdBusiness,
  MdAdd,
  MdSearch,
  MdRefresh,
  MdClose,
  MdCheckCircleOutline,
  MdOutlineError,
} from "react-icons/md";

export default function CreateDep() {
  const API = process.env.REACT_APP_API_URL;

  // =========================================================
  // STATE
  // =========================================================

  const [department, setDepartment] = useState([]);
  const [addDep, setAddDep] = useState("");
  const [branches, setBranches] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [showAddDep, setShowAddDep] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);

  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const [error, setError] = useState("");

  const [deleteId, setDeleteId] = useState(null);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const toast = useRef(null);

  // =========================================================
  // FETCH DEPARTMENTS
  // =========================================================

  const fecthDepartment = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await axios.get(
        `${API}/api/get/dep/`
      );

      setDepartment(res.data || []);
    } catch (err) {
      console.log("Error fetching departments:", err);

      setError(
        "Unable to load departments. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {
    fecthDepartment();
    axios.get(`${API}/api/get/branches/`)
      .then((res) => setBranches(res.data || []))
      .catch(() => setError("Unable to load locations. Please refresh."));
  }, []);

  // =========================================================
  // ADD DEPARTMENT
  // =========================================================

  const AddDepartment = async () => {
    const departmentName = addDep.trim();

    if (!departmentName) {
      toast.current?.show({
        severity: "warn",
        summary: "Department Required",
        detail: "Please enter a department name.",
        life: 3000,
      });

      return;
    }

    if (!selectedBranches.length) {
      toast.current?.show({ severity: "warn", summary: "Location Required", detail: "Select at least one location.", life: 3000 });
      return;
    }

    try {
      setAdding(true);

      const res = await axios.post(
        `${API}/api/add/dep/`,
        {
          name: departmentName,
          branch_ids: selectedBranches,
        }
      );

      console.log("dep", res.data);

      setAddDep("");
      setSelectedBranches([]);

      await fecthDepartment();

      setShowAddDep(false);

      toast.current?.show({
        severity: "success",
        summary: "Department Added",
        detail: "Department added successfully.",
        life: 3000,
      });
    } catch (err) {
      console.log("Add department error:", err);

      toast.current?.show({
        severity: "error",
        summary: "Add Failed",
        detail:
          err.response?.data?.error || "Unable to add department. Please try again.",
        life: 3000,
      });
    } finally {
      setAdding(false);
    }
  };

  // =========================================================
  // DELETE DEPARTMENT
  // =========================================================

  const delDepartment = async () => {
    if (!deleteId) return;

    try {
      await axios.delete(
        `${API}/api/deldep/${deleteId}/`
      );

      await fecthDepartment();

      setConfirmVisible(false);
      setDeleteId(null);

      toast.current?.show({
        severity: "success",
        summary: "Department Deleted",
        detail: "Department deleted successfully.",
        life: 3000,
      });
    } catch (err) {
      console.log("Delete department error:", err);

      setConfirmVisible(false);
      setDeleteId(null);

      toast.current?.show({
        severity: "error",
        summary: "Delete Failed",
        detail:
          "Unable to delete department. Please try again.",
        life: 3000,
      });
    }
  };

  // =========================================================
  // SEARCH
  // =========================================================

  const filteredDepartments = useMemo(() => {
    const search = searchTerm.toLowerCase().trim();

    return department.filter((dep) => {
      const matchesLocation = !locationFilter || (dep.branches || []).some(
        (branch) => String(branch.id) === locationFilter
      );
      const matchesSearch = (
        String(dep.id || "")
          .toLowerCase()
          .includes(search) ||
        String(dep.name || "")
          .toLowerCase()
          .includes(search)
      );
      return matchesLocation && matchesSearch;
    });
  }, [department, searchTerm, locationFilter]);

  useEffect(() => {
    setFirst(0);
  }, [department, searchTerm, locationFilter]);

  // =========================================================
  // CLEAR SEARCH
  // =========================================================

  const clearSearch = () => {
    setSearchTerm("");
  };

  const clearFilters = () => {
    clearSearch();
    setLocationFilter("");
  };

  // =========================================================
  // DEPARTMENT NAME TEMPLATE
  // =========================================================

  const departmentNameTemplate = (rowData) => {
    return (
      <div className="flex items-center gap-3">

        <div
          className="
            w-9
            h-9
            rounded-lg
            bg-sky-50
            border
            border-sky-100
            text-sky-600
            flex
            items-center
            justify-center
            flex-shrink-0
          "
        >
          <MdBusiness size={19} />
        </div>

        <span className="text-sm font-semibold text-slate-800">
          {rowData.name || "—"}
        </span>

      </div>
    );
  };

  // =========================================================
  // ID TEMPLATE
  // =========================================================

  const idTemplate = (rowData) => {
    return (
      <span className="text-xs font-semibold text-slate-500">
        #{rowData.id}
      </span>
    );
  };

  // =========================================================
  // ACTION TEMPLATE
  // =========================================================

  const actionTemplate = (rowData) => {
    return (
      <button
  type="button"
  title="Delete department"
  disabled={true}
  className="
    inline-flex
    items-center
    justify-center
    gap-1.5
    px-2.5
    py-1.5
    rounded-lg
    border
    border-gray-300
    bg-gray-100
    text-gray-400
    text-xs
    font-medium
    cursor-not-allowed
    opacity-60
  "
>
  <MdDelete size={16} />
  Delete
</button>
    );
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="w-full mt-10 min-h-screen bg-slate-50">

      <div className="w-full max-w-[1800px] mx-auto">

        {/* =================================================
            PAGE HEADER
        ================================================= */}

        <div
          className="
            bg-white
            border
            border-slate-200
            rounded-xl
            shadow-sm
            px-4
            sm:px-5
            lg:px-6
            py-4
            sm:py-5
            mb-5
          "
        >
          <div
            className="
              flex
              flex-col
              sm:flex-row
              sm:items-center
              sm:justify-between
              gap-4
            "
          >

            {/* TITLE */}

            <div className="flex items-center gap-3">

              <div
                className="
                  w-10
                  h-10
                  rounded-lg
                  bg-sky-50
                  text-sky-600
                  flex
                  items-center
                  justify-center
                  flex-shrink-0
                "
              >
                <MdBusiness size={22} />
              </div>

              <div>

                <h1 className="text-xl sm:text-2xl font-bold text-slate-800">
                  Departments
                </h1>

                {/* <p className="text-sm text-slate-500 mt-0.5">
                  Manage departments available in the system
                </p> */}

              </div>

            </div>

            {/* ADD BUTTON */}

            <button
              type="button"
              onClick={() => {
                setShowAddDep(!showAddDep);
                setAddDep("");
              }}
              className="
                inline-flex
                items-center
                justify-center
                gap-2
                w-full
                sm:w-auto
                px-4
                py-2.5
                rounded-lg
                bg-sky-600
                hover:bg-sky-700
                text-white
                text-sm
                font-semibold
                shadow-sm
                transition
              "
            >
              {showAddDep ? (
                <MdClose size={19} />
              ) : (
                <MdAdd size={19} />
              )}

              {showAddDep
                ? "Cancel"
                : "Add Department"}
            </button>

          </div>
        </div>

        {/* =================================================
            SUMMARY CARD
        ================================================= */}

        {/* <div
          className="
            grid
            grid-cols-1
            sm:grid-cols-2
            gap-3
            sm:gap-4
            mb-5
          "
        >

          <div
            className="
              bg-white
              border
              border-slate-200
              rounded-xl
              shadow-sm
              p-4
            "
          >

            <div className="flex items-start justify-between">

              <div>

                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Total Departments
                </p>

                <p className="mt-2 text-2xl font-bold text-slate-800">
                  {department.length}
                </p>

                <p className="mt-1 text-xs text-slate-400">
                  Departments currently registered
                </p>

              </div>

              <div
                className="
                  w-10
                  h-10
                  rounded-lg
                  bg-sky-50
                  text-sky-600
                  flex
                  items-center
                  justify-center
                "
              >
                <MdBusiness size={21} />
              </div>

            </div>

          </div>

          <div
            className="
              bg-white
              border
              border-slate-200
              rounded-xl
              shadow-sm
              p-4
            "
          >

            <div className="flex items-start justify-between">

              <div>

                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Current View
                </p>

                <p className="mt-2 text-2xl font-bold text-slate-800">
                  {filteredDepartments.length}
                </p>

                <p className="mt-1 text-xs text-slate-400">
                  Departments matching your search
                </p>

              </div>

              <div
                className="
                  w-10
                  h-10
                  rounded-lg
                  bg-emerald-50
                  text-emerald-600
                  flex
                  items-center
                  justify-center
                "
              >
                <MdCheckCircleOutline size={21} />
              </div>

            </div>

          </div>

        </div> */}

        {/* =================================================
            ADD DEPARTMENT FORM
        ================================================= */}

        {showAddDep && (

          <div
            className="
              bg-white
              border
              border-sky-200
              rounded-xl
              shadow-sm
              p-4
              sm:p-5
              mb-5
            "
          >

            <div className="flex items-center gap-2 mb-4">

              <div
                className="
                  w-8
                  h-8
                  rounded-lg
                  bg-sky-50
                  text-sky-600
                  flex
                  items-center
                  justify-center
                "
              >
                <MdAdd size={18} />
              </div>

              <div>

                <h2 className="text-sm font-semibold text-slate-800">
                  Add Department
                </h2>

                <p className="text-xs text-slate-500">
                  Enter a name for the new department
                </p>

              </div>

            </div>

            <div
              className="
                flex
                flex-col
                sm:flex-row
                gap-3
              "
            >

              <div className="flex-1">
                <label htmlFor="department-locations" className="block text-xs font-semibold text-slate-600 mb-1.5">Locations</label>
                <MultiSelect inputId="department-locations" value={selectedBranches}
                  options={branches} optionLabel="name" optionValue="id"
                  onChange={(e) => setSelectedBranches(e.value)} display="chip"
                  placeholder="Select one or more locations" className="w-full" disabled={adding} />
              </div>

              {/* INPUT */}

              <div className="flex-1">

                <label
                  htmlFor="department-name"
                  className="
                    block
                    text-xs
                    font-semibold
                    text-slate-600
                    mb-1.5
                  "
                >
                  Department Name
                </label>

                <input
                  id="department-name"
                  type="text"
                  placeholder="Enter department name"
                  value={addDep}
                  onChange={(e) =>
                    setAddDep(e.target.value)
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      AddDepartment();
                    }
                  }}
                  className="
                    w-full
                    h-10
                    px-3
                    rounded-lg
                    border
                    border-slate-200
                    bg-slate-50
                    text-sm
                    text-slate-700
                    placeholder:text-slate-400
                    outline-none
                    focus:bg-white
                    focus:border-sky-400
                    focus:ring-2
                    focus:ring-sky-100
                    transition
                  "
                />

              </div>

              {/* SAVE */}

              <div className="flex items-end">

                <button
                  type="button"
                  onClick={AddDepartment}
                  disabled={adding}
                  className="
                    inline-flex
                    items-center
                    justify-center
                    gap-2
                    w-full
                    sm:w-auto
                    h-10
                    px-5
                    rounded-lg
                    bg-sky-600
                    hover:bg-sky-700
                    disabled:bg-slate-300
                    disabled:cursor-not-allowed
                    text-white
                    text-sm
                    font-semibold
                    transition
                  "
                >
                  {adding ? (
                    <>
                      <span
                        className="
                          w-4
                          h-4
                          border-2
                          border-white/40
                          border-t-white
                          rounded-full
                          animate-spin
                        "
                      />

                      Saving...
                    </>
                  ) : (
                    <>
                      <MdCheckCircleOutline size={18} />
                      Save Department
                    </>
                  )}
                </button>

              </div>

            </div>

          </div>

        )}

        {/* =================================================
            SEARCH
        ================================================= */}

        <div
          className="
            
            rounded-xl
            
            p-4
            mb-5
          "
        >

          {/* <div className="flex items-center gap-2 mb-3">

            <MdSearch
              size={18}
              className="text-slate-500"
            />

            <h2 className="text-sm font-semibold text-slate-700">
              Department Directory
            </h2>

          </div> */}

          <div className="flex flex-col sm:flex-row gap-3">

            <div className="w-full sm:w-60">
              <label htmlFor="department-location-filter" className="sr-only">
                Filter by location
              </label>
              <select
                id="department-location-filter"
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition"
              >
                <option value="">All locations</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>

            {/* SEARCH INPUT */}

            <div className="relative flex-1">

              <MdSearch
                size={19}
                className="
                  absolute
                  left-3
                  top-1/2
                  -translate-y-1/2
                  text-slate-400
                "
              />

              <input
                type="text"
                value={searchTerm}
                onChange={(e) =>
                  setSearchTerm(e.target.value)
                }
                placeholder="Search department..."
                className="
                  w-full
                  h-10
                  pl-10
                  pr-10
                  rounded-lg
                  border
                  border-slate-200
                  bg-white
                  text-sm
                  text-slate-700
                  placeholder:text-slate-400
                  outline-none
                  focus:bg-white
                  focus:border-sky-400
                  focus:ring-2
                  focus:ring-sky-100
                  transition
                "
              />

              {searchTerm && (

                <button
                  type="button"
                  onClick={clearSearch}
                  className="
                    absolute
                    right-2
                    top-1/2
                    -translate-y-1/2
                    w-7
                    h-7
                    flex
                    items-center
                    justify-center
                    rounded-md
                    text-slate-400
                    hover:bg-slate-200
                    hover:text-slate-600
                  "
                >
                  <MdClose size={17} />
                </button>

              )}

            </div>

            {(searchTerm || locationFilter) && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg border border-slate-200 bg-white text-slate-600 text-sm font-medium hover:bg-slate-50 transition"
              >
                <MdClose size={18} />
                Clear Filters
              </button>
            )}

            {/* REFRESH */}

            <button
              type="button"
              onClick={fecthDepartment}
              disabled={loading}
              className="
                inline-flex
                items-center
                justify-center
                gap-2
                h-10
                px-4
                rounded-lg
                border
                border-slate-200
                bg-white
                text-slate-600
                text-sm
                font-medium
                hover:bg-slate-50
                disabled:opacity-50
                transition
              "
            >

              <MdRefresh
                size={18}
                className={
                  loading ? "animate-spin" : ""
                }
              />

              Refresh

            </button>

          </div>

        </div>

        {/* =================================================
            ERROR
        ================================================= */}

        {error && !loading && (

          <div
            className="
              bg-white
              border
              border-red-200
              rounded-xl
              shadow-sm
              p-8
              mb-5
              text-center
            "
          >

            <div
              className="
                mx-auto
                w-12
                h-12
                rounded-full
                bg-red-50
                text-red-500
                flex
                items-center
                justify-center
              "
            >
              <MdOutlineError size={24} />
            </div>

            <h3 className="mt-4 text-sm font-semibold text-slate-800">
              Unable to load departments
            </h3>

            <p className="mt-1 text-sm text-slate-500">
              {error}
            </p>

            <button
              type="button"
              onClick={fecthDepartment}
              className="
                mt-4
                inline-flex
                items-center
                gap-2
                px-4
                py-2
                rounded-lg
                bg-sky-600
                hover:bg-sky-700
                text-white
                text-sm
                font-medium
              "
            >
              <MdRefresh size={17} />
              Try Again
            </button>

          </div>

        )}

        {/* =================================================
            TABLE
        ================================================= */}

        {!error && (

          <div
            className="
              bg-white
              border
              border-slate-200
              rounded-xl
              shadow-sm
              overflow-hidden
            "
          >

            {/* TABLE HEADER */}

            <div
              className="
                px-4
                sm:px-5
                py-3.5
                border-b
                border-slate-200
                flex
                flex-col
                sm:flex-row
                sm:items-center
                sm:justify-between
                gap-2
              "
            >

              <div>

                <h2 className="text-sm font-semibold text-slate-800">
                  Departments
                </h2>

                <p className="text-xs text-slate-500 mt-0.5">
                  View and manage registered departments
                </p>

              </div>

              <div className="text-xs text-slate-500">
                {filteredDepartments.length} records
              </div>

            </div>

            {/* LOADING */}

            {loading ? (

              <div className="p-8">

                <div className="space-y-4 animate-pulse">

                  {[1, 2, 3, 4, 5].map((item) => (

                    <div
                      key={item}
                      className="
                        grid
                        grid-cols-3
                        gap-4
                        items-center
                      "
                    >

                      <div className="h-4 bg-slate-100 rounded" />

                      <div className="h-4 bg-slate-100 rounded" />

                      <div className="h-8 bg-slate-100 rounded" />

                    </div>

                  ))}

                </div>

                <p className="text-center text-xs text-slate-400 mt-5">
                  Loading departments...
                </p>

              </div>

            ) : filteredDepartments.length === 0 ? (

              /* EMPTY STATE */

              <div className="py-14 px-5 text-center">

                <div
                  className="
                    mx-auto
                    w-14
                    h-14
                    rounded-full
                    bg-slate-100
                    text-slate-400
                    flex
                    items-center
                    justify-center
                  "
                >
                  <MdBusiness size={28} />
                </div>

                <h3 className="mt-4 text-sm font-semibold text-slate-800">
                  No departments found
                </h3>

                <p className="mt-1 text-sm text-slate-500">
                  No departments match your current filters.
                </p>

                {(searchTerm || locationFilter) && (

                  <button
                    type="button"
                    onClick={clearFilters}
                    className="
                      mt-4
                      inline-flex
                      items-center
                      gap-2
                      px-4
                      py-2
                      rounded-lg
                      border
                      border-slate-200
                      bg-white
                      text-slate-600
                      text-sm
                      font-medium
                      hover:bg-slate-50
                    "
                  >
                    <MdRefresh size={17} />
                    Clear Filters
                  </button>

                )}

              </div>

            ) : (

              /* DATA TABLE */

              <div className="w-full overflow-x-auto">

                <DataTable
                  value={filteredDepartments}
                  paginator
                  first={first}
                  rows={rows}
                  onPage={(e) => {
                    setFirst(e.first);
                    setRows(e.rows);
                  }}
                  rowsPerPageOptions={[10, 25, 50]}
                  className="w-full professional-department-table"
                  tableStyle={{
                    minWidth: "650px",
                  }}
                  rowHover
                  removableSort
                  paginatorTemplate="
                    FirstPageLink
                    PrevPageLink
                    PageLinks
                    NextPageLink
                    LastPageLink
                    RowsPerPageDropdown
                  "
                >

                  {/* ID */}

                  <Column
                    field="id"
                    header="ID"
                    sortable
                    body={idTemplate}
                    style={{
                      width: "120px",
                    }}
                  />

                  {/* DEPARTMENT */}

                  <Column
                    field="name"
                    header="Department"
                    sortable
                    body={departmentNameTemplate}
                    style={{
                      width: "55%",
                    }}
                  />

                  <Column header="Locations"
                    body={(row) => (row.branches || []).map((branch) => branch.name).join(", ")}
                  />

                  {/* ACTION */}

                  <Column
                    header="Actions"
                    body={actionTemplate}
                    style={{
                      width: "180px",
                    }}
                  />

                </DataTable>

              </div>

            )}

          </div>

        )}

      </div>

      {/* =====================================================
          TOAST
      ===================================================== */}

      <Toast ref={toast} />

      {/* =====================================================
          DELETE CONFIRMATION
      ===================================================== */}

      <ConfirmDialog
        visible={confirmVisible}
        onHide={() => {
          setConfirmVisible(false);
          setDeleteId(null);
        }}
        message="Are you sure you want to delete this department? This action cannot be undone."
        header="Delete Department"
        icon="pi pi-exclamation-triangle"
        accept={delDepartment}
        reject={() => {
          setConfirmVisible(false);
          setDeleteId(null);

          toast.current?.show({
            severity: "warn",
            summary: "Cancelled",
            detail: "Delete cancelled.",
            life: 2500,
          });
        }}
        acceptClassName="p-button-danger"
        rejectClassName="p-button-secondary"
      />

      {/* =====================================================
          PRIME REACT TABLE STYLING
      ===================================================== */}

      <style>{`

        .professional-department-table
          .p-datatable-thead > tr > th {

          background: #f8fafc;
          color: #475569;
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          padding: 0.85rem 0.9rem;
          border-color: #e2e8f0;
          white-space: nowrap;
        }

        .professional-department-table
          .p-datatable-tbody > tr > td {

          padding: 0.85rem 0.9rem;
          border-color: #f1f5f9;
          color: #475569;
          font-size: 0.875rem;
          vertical-align: middle;
        }

        .professional-department-table
          .p-datatable-tbody > tr:hover {

          background: #f8fafc;
        }

        .professional-department-table
          .p-paginator {

          background: #ffffff;
          border: 0;
          border-top: 1px solid #e2e8f0;
          padding: 0.65rem 0.85rem;
          justify-content: flex-end;
          gap: 0.15rem;
        }

        .professional-department-table
          .p-paginator
          .p-paginator-page,
        .professional-department-table
          .p-paginator
          .p-paginator-next,
        .professional-department-table
          .p-paginator
          .p-paginator-prev,
        .professional-department-table
          .p-paginator
          .p-paginator-first,
        .professional-department-table
          .p-paginator
          .p-paginator-last {

          min-width: 2rem;
          height: 2rem;
          border-radius: 0.5rem;
          font-size: 0.75rem;
        }

        .professional-department-table
          .p-paginator
          .p-highlight {

          background: #0284c7;
          color: white;
        }

        .professional-department-table
          .p-dropdown {

          height: 2rem;
          border-radius: 0.5rem;
          border-color: #e2e8f0;
          font-size: 0.75rem;
        }

        .professional-department-table
          .p-sortable-column-icon {

          width: 0.8rem;
          height: 0.8rem;
        }

        .professional-department-table
          .p-datatable-wrapper {

          scrollbar-width: thin;
        }

        @media (max-width: 640px) {

          .professional-department-table
            .p-datatable-thead > tr > th {

            padding: 0.7rem;
          }

          .professional-department-table
            .p-datatable-tbody > tr > td {

            padding: 0.7rem;
          }

          .professional-department-table
            .p-paginator {

            justify-content: center;
            flex-wrap: wrap;
          }
        }

      `}</style>

    </div>
  );
}
